<template>
  <validation-observer
    ref="form"
    :class="{ 'signature-readonly': isReadOnly }"
    novalidate
  >
    <div class="signature-disclaimer">
      <p>
        Este informe ha sido preparado por la Unidad de Evaluación Vocacional
        del Departamento de Educación de Puerto Rico. Su propósito es aportar
        información relevante para apoyar el proceso de transición del
        estudiante desde la etapa escolar hacia la vida adulta. Los resultados
        obtenidos no constituyen ni sustituyen un diagnóstico clínico,
        psicológico, médico o psiquiátrico, y se utilizan exclusivamente para
        apoyar la toma de decisiones educativas, la planificación de apoyos y la
        definición de metas académicas y laborales, conforme a los criterios
        éticos y técnicos aplicables.
      </p>
      <p>
        Los resultados aquí consignados no deben interpretarse como valores
        absolutos, sino como indicadores que orientan la planificación educativa
        y vocacional en el momento. Las recomendaciones incluidas se fundamentan
        en el análisis de la información recopilada durante el proceso de
        evaluación, y deben considerarse como parte de un esfuerzo continuo y
        flexible que puede enriquecerse con futuros procesos evaluativos.
      </p>
      <p>
        Este informe constituye un componente importante dentro del proceso de
        planificación educativa individualizada del estudiante con el fin de que
        los servicios de transición sean pertinentes y se alineen con las metas
        educativas, ocupacionales y de vida independiente del estudiante. La
        información contenida debe ser considerada por el Comité de Padres y
        Maestros (COMPU) como parte del proceso de revisión del Programa
        Educativo Individualizado (PEI) o del Programa de Servicios Educativos
        correspondiente.
      </p>
      <p>
        También se sugiere que el COMPU evalúe la ubicación educativa más
        adecuada para el estudiante, considerando la disponibilidad de recursos
        instructivos y personal capacitado que pueda atender sus necesidades de
        manera integral.
      </p>
    </div>

    <validation-provider
      v-slot="{ errors }"
      name="acceptedTermsResponsibilities"
      rules="required"
    >
      <custom-checkbox-single-option
        v-model="model.acceptedTermsResponsibilities"
        class="custom-single-option signature-terms__checkbox"
        custom-error-msg="Debe aceptar los términos y responsabilidades para continuar."
        :disabled="isReadOnly"
        :error="errors[0]"
        option-text="Acepto los términos y responsabilidades"
      />
    </validation-provider>

    <hr class="my-4" />

    <sign-plan
      ref="signPlanRef"
      class="my-4"
      :disabled="isReadOnly"
      :is-electronic-signature.sync="isElectronicSignature"
      question-label="¿Cuál será el mecanismo que se estará utilizando para firmar el Informe de Evaluación Vocacional?"
    />

    <div v-if="isElectronic" class="signature-flow mt-3">
      <section-title content="Configuración de Firma Digital" has-line />
      <div class="signature-flow__panel text-center">
        <signature-hub-info
          class="signature-hub-info--embedded"
          :signature-info="signatureHubInfo"
          @configure-sign="handleConfigureSignatures"
        />
      </div>
    </div>

    <div v-if="isManual" class="signature-flow mt-3">
      <template v-if="!manualSignerAdded && !isReadOnly">
        <validation-observer ref="manualForm" slim>
          <div class="signature-manual row align-items-start">
            <validation-provider
              v-slot="{ errors }"
              class="col-12 col-md-3"
              :rules="nameRules"
            >
              <label class="data-label">Nombre del Evaluador:</label>
              <input
                v-model="model.evaluatorName"
                class="form-control"
                :class="{ 'is-invalid': errors[0] }"
                :disabled="isReadOnly"
                maxlength="50"
              />
              <error
                v-if="errors[0]"
                :message="nameError"
                variant="no-border"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              class="col-12 col-md-1"
              :rules="initialRules"
            >
              <label class="data-label">Inicial:</label>
              <input
                v-model="model.evaluatorInitialName"
                class="form-control"
                :class="{ 'is-invalid': errors[0] }"
                :disabled="isReadOnly"
                maxlength="1"
              />
              <error
                v-if="errors[0]"
                message="Debe contener solo una letra."
                variant="no-border"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              class="col-12 col-md-3"
              :rules="nameRules"
            >
              <label class="data-label">Apellido Paterno:</label>
              <input
                v-model="model.evaluatorLastName"
                class="form-control"
                :class="{ 'is-invalid': errors[0] }"
                :disabled="isReadOnly"
                maxlength="50"
              />
              <error
                v-if="errors[0]"
                :message="nameError"
                variant="no-border"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              class="col-12 col-md-3"
              :rules="optionalNameRules"
            >
              <label class="data-label">Apellido Materno:</label>
              <input
                v-model="model.evaluatorSecondLastName"
                class="form-control"
                :class="{ 'is-invalid': errors[0] }"
                :disabled="isReadOnly"
                maxlength="50"
              />
              <error
                v-if="errors[0]"
                :message="nameError"
                variant="no-border"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              class="col-12 col-md-2"
              rules="required"
            >
              <custom-date-picker
                id="evaluatorDate"
                v-model="model.evaluatorDate"
                class-container="w-100"
                custom-error-msg="El campo es requerido."
                :disabled="isReadOnly"
                :error="errors[0]"
                format="dd/MM/yyyy"
                label="Fecha:"
                placeholder="DD/MM/YYYY"
              />
            </validation-provider>

            <div class="col-12 col-md-auto signature-manual__action">
              <action-button
                :disabled="!canAddManualSigner || isReadOnly"
                i="fas fa-file-medical"
                text="Añadir"
                variant="success"
                @click="addManualSigner"
              />
            </div>
          </div>
        </validation-observer>
      </template>

      <div v-if="manualSignerAdded" class="signature-manual-summary row">
        <div class="col-12 col-md">
          <span class="signature-flow__label">Nombre del Evaluador:</span>
          <strong>{{ model.evaluatorName }}</strong>
        </div>
        <div class="col-12 col-md-1">
          <span class="signature-flow__label">Inicial:</span>
          <strong>{{ model.evaluatorInitialName | empty }}</strong>
        </div>
        <div class="col-12 col-md">
          <span class="signature-flow__label">Apellido Paterno:</span>
          <strong>{{ model.evaluatorLastName }}</strong>
        </div>
        <div class="col-12 col-md">
          <span class="signature-flow__label">Apellido Materno:</span>
          <strong>{{ model.evaluatorSecondLastName | empty }}</strong>
        </div>
        <div class="col-12 col-md">
          <span class="signature-flow__label">Fecha:</span>
          <strong>{{ model.evaluatorDate | date }}</strong>
        </div>
        <button
          v-if="!isReadOnly"
          class="signature-manual-summary__remove"
          type="button"
          @click="resetManualSignature"
        >
          <i class="fas fa-times-circle" />
        </button>
      </div>

      <template v-if="manualSignerAdded">
        <section-title class="mt-3" has-line title="Documento" />
        <div
          v-if="!hasDownloadedTemplate"
          class="signature-flow__panel text-center"
        >
          <action-button
            i="fas fa-file-download"
            text="Descargar Informe de Evaluación Vocacional"
            variant="outline-success"
            @click="downloadTemplate"
          />
        </div>
        <div v-else class="signature-document-row row">
          <div class="col-md-4">
            <div class="color-gray-6 font-14 font-weight-normal">
              Documento:
            </div>
            <div class="color-black font-16 font-weight-normal">
              Informe de Evaluación Vocacional
            </div>
          </div>
          <div class="col-md-5">
            <div class="color-gray-6 font-14 font-weight-normal">
              Archivo:
            </div>
            <div v-if="hasSignedDocument && !editingDocument">
              {{ model.signedDocumentName | empty }}
            </div>
            <validation-provider v-else v-slot="{ errors }" rules="required">
              <custom-file-input
                v-model="selectedDocument"
                :accept="acceptedDocumentTypes"
                :attachment-id="model.signedDocumentId"
                custom-error-msg="Favor de anejar el documento firmado."
                :disabled="isReadOnly"
                display-style="normal"
                :error="errors[0]"
                label-position="left"
                required
                @input="saveSignedDocumentFile"
              />
            </validation-provider>
          </div>
          <div class="col-md-3 d-flex justify-content-end">
            <div v-if="hasSignedDocument && !editingDocument">
              <action-button
                v-if="!isReadOnly"
                class="mr-2 mt-1"
                i="fas fa-times text-danger mr-n1 pr-1"
                variant="outline-danger"
                @click="resetSignedDocument"
              />
              <action-button
                class="mt-1"
                i="fas fa-file-download text-white mr-n1 pr-1"
                variant="success"
                @click="downloadSignedDocument"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </validation-observer>
</template>

<script>
import { serialize as objectToFormData } from 'object-to-formdata';
import alertMixin from '@/mixins/alert.mixin.js';
import { copy } from '@/utils/objUtils';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import _generalApi from '@/modules/app/api';
import ActionButton from 'ActionButton';
import CustomCheckboxSingleOption from 'custom-checkbox-single-option';
import CustomDatePicker from 'custom-date-picker';
import CustomFileInput from 'CustomFileInput';
import Error from '@/components/common/Error';
import SectionTitle from '@/shared/components/SectionTitle.vue';
import { SignPlan } from '@/views/provider/referral-management/search/components/sign-plan';
import { SignatureHubInfo } from '@quisit/signature-hub';
import {
  getReport,
  getSignature,
  save as saveEvaluation,
  saveSignedDocument,
} from '@/modules/vocational-evaluation/api.js';
import {
  getSignatureHubInfo as getSignatureHubInfoApi,
  removeSignatureHub,
} from '@/modules/signatureHub/api';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes.js';
import Statuses from '@/utils/constants/vocationalEducation';
import DigitalSignatureDocumentStatus from '@/utils/constants/digitalSignatureDocumentStatus';

const SignatureMechanisms = Object.freeze({
  Electronic: 1,
  Manual: 2,
});

export default {
  name: 'VocationalEducationSignature',
  components: {
    ActionButton,
    CustomCheckboxSingleOption,
    CustomDatePicker,
    CustomFileInput,
    Error,
    SectionTitle,
    SignPlan,
    SignatureHubInfo,
    ValidationObserver,
    ValidationProvider,
  },
  filters: {
    empty(value) {
      return value || '-';
    },
  },
  mixins: [alertMixin],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    initialModel: {
      type: Object,
      required: false,
      default: () => null,
    },
    keepUpdates: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isReadOnly: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    statusId: {
      type: Number,
      required: false,
      default: () => Statuses.PendingSignature,
    },
  },
  data() {
    return {
      acceptedDocumentTypes: '.pdf,.jpg,.jpeg,.png',
      editingDocument: false,
      hasDownloadedTemplate: false,
      lettersPattern: /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ ]+$/,
      singleLetterPattern: /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]$/,
      manualSignerAdded: false,
      previousMechanismId: null,
      skipMechanismWatcher: false,
      selectedDocument: null,
      signatureHubInfo: null,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    filled() {
      return this.initialModel?.filled ?? false;
    },
    isElectronicSignature: {
      get() {
        if (this.isElectronic) return true;
        if (this.isManual) return false;
        return null;
      },
      set(value) {
        const signatureMechanismId =
          value === true
            ? SignatureMechanisms.Electronic
            : SignatureMechanisms.Manual;

        this.model = {
          ...this.model,
          signatureMechanismId,
        };
      },
    },
    isElectronic() {
      return (
        Number(this.model.signatureMechanismId) ===
        SignatureMechanisms.Electronic
      );
    },
    isManual() {
      return (
        Number(this.model.signatureMechanismId) === SignatureMechanisms.Manual
      );
    },
    nameRules() {
      return {
        required: true,
        min: 2,
        max: 50,
        regex: this.lettersPattern,
      };
    },
    optionalNameRules() {
      return {
        min: 2,
        max: 50,
        regex: this.lettersPattern,
      };
    },
    initialRules() {
      return {
        max: 1,
        regex: this.singleLetterPattern,
      };
    },
    nameError() {
      return 'Debe contener solo letras y tener entre 2 y 50 caracteres.';
    },
    canAddManualSigner() {
      return (
        this.isValidName(this.model.evaluatorName, true) &&
        this.isValidInitial(this.model.evaluatorInitialName) &&
        this.isValidName(this.model.evaluatorLastName, true) &&
        this.isValidName(this.model.evaluatorSecondLastName, false) &&
        Boolean(this.model.evaluatorDate)
      );
    },
    hasSignedDocument() {
      return Boolean(this.model.signedDocumentId);
    },
    signatureHubDocumentStatusId() {
      return Number(this.signatureHubInfo?.documentInfo?.documentStatusId);
    },
    hasElectronicSignedDocument() {
      return (
        this.signatureHubDocumentStatusId ===
        DigitalSignatureDocumentStatus.Signed
      );
    },
    hasElectronicFlow() {
      return Boolean(
        this.signatureHubInfo?.signatureHubId ||
          this.signatureHubInfo?.documentId
      );
    },
    canCompleteSignature() {
      return this.isManual
        ? this.hasSignedDocument
        : this.isElectronic && this.hasElectronicSignedDocument;
    },
  },
  watch: {
    canCompleteSignature: {
      immediate: true,
      handler(value) {
        this.$emit('update:can-complete-signature', value);
      },
    },
    'model.signatureMechanismId': {
      async handler(value, oldValue) {
        if (this.skipMechanismWatcher || value === oldValue) return;
        await this.handleMechanismChange(oldValue, value);
      },
    },
  },
  beforeDestroy() {
    this.$emit('update:initialModel', this.model);
  },
  async mounted() {
    this.$emit('update:disabled', true);
    await this.get();
    this.$emit('update:disabled', false);
  },
  methods: {
    setModelFrom(value) {
      const data = copy(value);
      this.model = data;
      this.manualSignerAdded = this.hasManualSignerData(data);
      this.previousMechanismId = data.signatureMechanismId;
      this.$emit('update:initialModel', data);
    },
    hasManualSignerData(value) {
      return Boolean(
        value?.evaluatorName && value?.evaluatorLastName && value?.evaluatorDate
      );
    },
    isValidName(value, required) {
      if (!value) return !required;

      return (
        value.length >= 2 &&
        value.length <= 50 &&
        this.lettersPattern.test(value)
      );
    },
    isValidInitial(value) {
      return !value || this.singleLetterPattern.test(value);
    },
    async get() {
      if (this.keepUpdates && this.filled) {
        this.setModelFrom(this.initialModel);
        return;
      }

      if (!this.appointmentId) return;
      const { data } = await getSignature({
        id: this.id,
        appointmentId: this.appointmentId,
      });
      this.setModelFrom({ ...data, filled: true });
      this.hasDownloadedTemplate = Boolean(data?.signedDocumentId);
      if (this.isElectronic) await this.getSignatureHubInfo();
    },
    async validate() {
      const formIsValid = await this.$refs.form.validate();
      const signPlanIsValid = await this.$refs.signPlanRef.validate();
      return formIsValid && signPlanIsValid;
    },
    async handleMechanismChange(previousValue, nextValue) {
      const previousMechanismId = Number(
        previousValue || this.previousMechanismId
      );
      const nextMechanismId = Number(
        nextValue || this.model.signatureMechanismId
      );

      if (
        previousMechanismId &&
        previousMechanismId !== nextMechanismId &&
        !(await this.confirmMechanismChange(
          previousMechanismId,
          nextMechanismId
        ))
      ) {
        this.skipMechanismWatcher = true;
        this.model = {
          ...this.model,
          signatureMechanismId: previousMechanismId,
        };
        await this.$nextTick();
        this.skipMechanismWatcher = false;
        return;
      }

      this.selectedDocument = null;
      this.editingDocument = false;
      this.manualSignerAdded = this.hasManualSignerData(this.model);
      if (this.isElectronic) await this.getSignatureHubInfo();
      this.previousMechanismId = nextMechanismId;
      this.$emit('update:can-complete-signature', this.canCompleteSignature);
    },
    async addManualSigner() {
      const valid = await this.$refs.manualForm.validate();
      if (!valid && !this.canAddManualSigner) return;
      await this.saveSignatureInfo();
      this.manualSignerAdded = true;
      this.hasDownloadedTemplate = false;
    },
    async saveSignatureInfo() {
      await saveEvaluation({
        tab: 'Signature',
        ...copy(this.model),
        id: this.id && this.id > 0 ? Number(this.id) : null,
        appointmentId: Number(this.appointmentId),
        statusId: this.statusId,
        isSubmitted: false,
      });
      this.$emit('update:initialModel', copy(this.model));
    },
    async downloadReport() {
      try {
        await this.saveSignatureInfo();
        const response = await getReport(this.id);
        _generalApi.ConvertToBlob(response.data);
      } catch {
        this.ShowToast('', 'No fue posible descargar el informe.', 'error');
      }
    },
    async downloadTemplate() {
      await this.downloadReport();
      this.hasDownloadedTemplate = true;
    },
    async downloadSignedDocument() {
      await _generalApi.downloadDocument(this.model.signedDocumentId);
    },
    async resetManualSignature() {
      this.clearManualFlow();
      await this.saveSignatureInfo();
      this.$emit('update:can-complete-signature', this.canCompleteSignature);
    },
    async resetSignedDocument() {
      this.model = {
        ...this.model,
        signedDocumentId: null,
        signedDocumentName: null,
      };
      this.selectedDocument = null;
      this.editingDocument = false;
      await this.saveSignatureInfo();
      this.$emit('update:can-complete-signature', this.canCompleteSignature);
    },
    async confirmMechanismChange(previousMechanismId, nextMechanismId) {
      if (
        nextMechanismId === SignatureMechanisms.Electronic &&
        this.hasManualProgress()
      ) {
        const confirmed = await this.ShowCancelModal(
          'Confirmación',
          'Usted ya se encuentra en un proceso de configuración de firma manuscrita. ¿Está seguro que desea cambiar la configuración a electrónica?',
          'Sí',
          'No'
        );

        if (!confirmed) return false;
        this.clearManualFlow();
      }

      if (
        nextMechanismId === SignatureMechanisms.Manual &&
        previousMechanismId === SignatureMechanisms.Electronic &&
        this.hasElectronicFlow
      ) {
        const confirmed = await this.ShowCancelModal(
          'Confirmación',
          'Usted ya se encuentra en un proceso de configuración de firma digital. ¿Está seguro que desea cambiar la configuración a manuscrito?',
          'Sí',
          'No'
        );

        if (!confirmed) return false;
        if (!(await this.removeElectronicFlow())) return false;
      }

      await this.saveSignatureInfo();
      return true;
    },
    hasManualProgress() {
      return Boolean(
        this.model.evaluatorName ||
          this.model.evaluatorInitialName ||
          this.model.evaluatorLastName ||
          this.model.evaluatorSecondLastName ||
          this.model.evaluatorDate ||
          this.model.signedDocumentId ||
          this.selectedDocument ||
          this.hasDownloadedTemplate
      );
    },
    clearManualFlow() {
      this.model = {
        ...this.model,
        evaluatorName: null,
        evaluatorInitialName: null,
        evaluatorLastName: null,
        evaluatorSecondLastName: null,
        evaluatorDate: null,
        signedDocumentId: null,
        signedDocumentName: null,
      };
      this.selectedDocument = null;
      this.editingDocument = false;
      this.hasDownloadedTemplate = false;
      this.manualSignerAdded = false;
    },
    async removeElectronicFlow() {
      try {
        await removeSignatureHub(
          SignatureHubTypes.VOCATIONAL_EVALUATION,
          Number(this.id)
        );
        this.signatureHubInfo = null;
        this.$emit('update:signature-hub-info', null);
        return true;
      } catch (error) {
        this.ShowToast(
          '',
          error.response?.data?.message || error.message,
          'error'
        );
        return false;
      }
    },
    async saveSignedDocumentFile(file) {
      if (!file) return;
      const payload = objectToFormData(
        {
          id: this.id,
          file,
        },
        {
          indices: true,
          nullsAsUndefineds: true,
        }
      );

      try {
        const { data } = await saveSignedDocument(payload);
        this.model = {
          ...this.model,
          signedDocumentId: data?.id ?? data ?? this.model.signedDocumentId,
          signedDocumentName: file.name,
        };
        this.editingDocument = false;
        this.hasDownloadedTemplate = true;
        this.ShowToast('', 'Documento guardado exitosamente.', 'success');
        this.$emit('update:can-complete-signature', this.canCompleteSignature);
      } catch {
        this.ShowToast(
          '',
          'Ocurrió un error al intentar guardar el documento firmado.',
          'error'
        );
      }
    },
    async getSignatureHubInfo() {
      try {
        const { data } = await getSignatureHubInfoApi(
          SignatureHubTypes.VOCATIONAL_EVALUATION,
          Number(this.id)
        );
        this.signatureHubInfo = data;
        this.$emit('update:signature-hub-info', data);
      } catch {
        this.signatureHubInfo = null;
      }
    },
    async handleConfigureSignatures() {
      await this.saveSignatureInfo();
      this.$router.push({
        name: 'SignatureHub',
        params: {
          typeId: SignatureHubTypes.VOCATIONAL_EVALUATION,
          relatedId: this.id,
          from: 'vocationalEvaluation',
          fromData: {
            id: this.id,
            appointmentId: this.appointmentId,
          },
        },
        query: { tab: this.$route.query.tab },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.signature-disclaimer {
  color: #162326;
  line-height: 1.65;
}

.signature-terms__checkbox {
  margin-top: 1.75rem;
}

.signature-flow {
  background-color: transparent;
  padding: 0;
}

.signature-flow__panel {
  background-color: #f8f8f8;
  border-radius: 4px;
  margin: 1.5rem 0;
  padding: 1.35rem;
}

.signature-hub-info--embedded {
  ::v-deep > .mb-3 {
    display: none;
  }

  ::v-deep .box-container > .row {
    align-items: center;
    text-align: left;
  }

  ::v-deep .box-container > .row > .col {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  ::v-deep .box-container > .row > .col:last-child {
    align-items: flex-end;
  }

  ::v-deep .box-container__status-icon {
    flex: 0 0 auto;
    margin-right: 0.5rem;
  }

  ::v-deep .box-container__status-label {
    line-height: 1.2;
    white-space: nowrap;
  }
}

.signature-flow__empty {
  color: #6c757d;
}

.signature-flow__label {
  color: #6c757d;
  display: block;
  font-size: 12px;
}

.signature-manual {
  background-color: #f8f8f8;
  border-radius: 4px;
  margin: 0 0 1.5rem;
  padding: 1rem 0.75rem;
}

.signature-manual__action {
  padding-top: 26px;
}

.signature-manual-summary {
  align-items: center;
  background-color: #f8f8f8;
  border-radius: 4px;
  margin: 1.5rem 0;
  min-height: 72px;
  padding: 1rem 2.75rem 1rem 1rem;
  position: relative;
}

.signature-manual-summary__remove {
  background: transparent;
  border: 0;
  color: #ff5b5c;
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
}

.signature-document-row {
  align-items: center;
  background-color: #f8f8f8;
  border-radius: 4px;
  margin: 1.5rem 0 0;
  min-height: 78px;
  padding: 1rem;
}

.signature-document-row .col-md-3 {
  align-items: center;
}

.signature-readonly {
  ::v-deep .green-box,
  ::v-deep .custom-radio__extended .radio-button.bg-checked,
  ::v-deep .success {
    background-color: #294348 !important;
    border-color: #294348 !important;
  }

  ::v-deep .outline-success {
    border-color: #294348 !important;
    color: #294348 !important;
  }

  ::v-deep .outline-success:hover,
  ::v-deep .outline-success:focus,
  ::v-deep .outline-success:active {
    background-color: #294348 !important;
    border-color: #294348 !important;
    color: #ffffff !important;
  }

  ::v-deep .green-box .input,
  ::v-deep .green-box .input::after {
    color: #294348 !important;
  }

  ::v-deep .custom-radio__extended .radio-button.bg-checked .step-label,
  ::v-deep .custom-radio__extended .radio-button.bg-checked .rounded-circle i {
    color: #ffffff !important;
  }

  ::v-deep .custom-radio__extended .radio-button.bg-checked .rounded-circle {
    border-color: #ffffff !important;
  }
}

.data-label {
  color: #6c757d;
  font-size: 12px;
}
</style>
